import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Tv,
  Film,
  Tv2,
  Star,
  Settings,
  Radio,
  ChevronDown,
  User,
  SlidersHorizontal,
  CheckCircle2,
  RefreshCw,
  Shield,
  Smartphone,
  Download,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { APP_VERSION } from '../../version';

interface TVNavbarProps {
  onOpenProfiles: (targetProfile?: any, startInManageMode?: boolean) => void;
  onOpenSettings: () => void;
  onOpenSearch: () => void;
  onOpenAuth?: () => void;
  className?: string;
  hideMobileBottomBar?: boolean;
}

export const TVNavbar: React.FC<TVNavbarProps> = ({
  onOpenProfiles,
  onOpenSettings,
  onOpenSearch,
  onOpenAuth,
  className = '',
  hideMobileBottomBar = false,
}) => {
  const {
    activeTab,
    setActiveTab,
    stopPlayback,
    setActiveChannelCategory,
    setActiveMovieCategory,
    setActiveSeriesCategory,
    currentProfile,
    profiles,
    selectProfile,
    refreshPlaylists,
    authUser,
    isAdmin,
    showToast,
    openDevicesModal,
  } = useIPTV();

  const isMasterProfile = useMemo(() => {
    return !!(currentProfile?.isMaster || currentProfile?.id === 'prof-1' || currentProfile?.id === profiles[0]?.id);
  }, [currentProfile, profiles]);

  const [timeStr, setTimeStr] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleRefreshList = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshPlaylists();
    } finally {
      setTimeout(() => setIsRefreshing(false), 800);
    }
  };

  // Clear pending close timeout
  const cancelCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Start auto-close timer when focus or hover leaves the menu
  const startCloseTimer = (delay = 2500) => {
    cancelCloseTimer();
    closeTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
    }, delay);
  };

  const handleTabClick = (tabId: 'home' | 'live' | 'movies' | 'series' | 'favorites' | 'settings') => {
    stopPlayback();
    if (tabId === 'live') {
      setActiveChannelCategory('Favoritos');
    } else if (tabId === 'movies') {
      setActiveMovieCategory('Continuar Assistindo');
    } else if (tabId === 'series') {
      setActiveSeriesCategory('Continuar Assistindo');
    }
    setActiveTab(tabId);
  };

  useEffect(() => {
    const updateTime = () => {
      const d = new Date();
      setTimeStr(
        d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      cancelCloseTimer();
    };
  }, []);

  // When menu opens, automatically focus the first interactive item (itemRefs.current[0])
  useEffect(() => {
    if (isMenuOpen) {
      cancelCloseTimer();
      const timer = setTimeout(() => {
        const validItems = itemRefs.current.filter((el): el is HTMLElement => el !== null);
        if (validItems.length > 0 && validItems[0]) {
          validItems[0].focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    } else {
      cancelCloseTimer();
    }
  }, [isMenuOpen]);

  // Close menu on click outside or escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        cancelCloseTimer();
        setIsMenuOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isMenuOpen) return;

      if (e.key === 'Escape' || e.key === 'Back' || e.key === 'BrowserBack') {
        e.preventDefault();
        cancelCloseTimer();
        setIsMenuOpen(false);
        toggleBtnRef.current?.focus();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  // Handle keyboard navigation between dropdown items (Strict sequential loop)
  const handleMenuItemKeyDown = (index: number, e: React.KeyboardEvent) => {
    cancelCloseTimer();
    const validItems = itemRefs.current.filter((el): el is HTMLElement => el !== null);
    const currentIndex = validItems.indexOf(e.currentTarget as HTMLElement);

    if (e.key === 'ArrowDown' || e.key === 'Down') {
      e.preventDefault();
      e.stopPropagation();
      const nextIndex = (currentIndex + 1) % validItems.length;
      validItems[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp' || e.key === 'Up') {
      e.preventDefault();
      e.stopPropagation();
      if (currentIndex === 0) {
        toggleBtnRef.current?.focus();
      } else {
        const prevIndex = (currentIndex - 1 + validItems.length) % validItems.length;
        validItems[prevIndex]?.focus();
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'Left' || e.key === 'ArrowRight' || e.key === 'Right') {
      e.preventDefault();
      e.stopPropagation();
      setIsMenuOpen(false);
      toggleBtnRef.current?.focus();
    } else if (e.key === 'Escape' || e.key === 'Back' || e.key === 'BrowserBack') {
      e.preventDefault();
      e.stopPropagation();
      setIsMenuOpen(false);
      toggleBtnRef.current?.focus();
    }
  };

  const navTabs = [
    { id: 'live', label: 'Ao Vivo', icon: Tv, action: () => handleTabClick('live'), isRefresh: false },
    { id: 'movies', label: 'Filmes', icon: Film, action: () => handleTabClick('movies'), isRefresh: false },
    { id: 'series', label: 'Séries', icon: Tv2, action: () => handleTabClick('series'), isRefresh: false },
    { id: 'favorites', label: 'Favoritos', icon: Star, action: () => handleTabClick('favorites'), isRefresh: false },
  ] as const;

  return (
    <>
      <header className={`min-h-16 md:min-h-18 pt-[max(env(safe-area-inset-top,0px),14px)] pb-2 md:py-0 bg-zinc-950/95 border-b border-zinc-800/80 px-4 md:px-8 flex items-center justify-between select-none z-40 backdrop-blur-xl shrink-0 relative ${className}`}>
      {/* Brand / Logo (Vai para o Início / Home) */}
      <div
        tabIndex={0}
        role="button"
        onClick={() => handleTabClick('home')}
        onKeyDown={(e) => e.key === 'Enter' && handleTabClick('home')}
        className="flex items-center gap-2.5 md:gap-3 cursor-pointer group shrink-0 focus:outline-none focus:ring-2 focus:ring-theme-accent/60 rounded-xl p-1"
        title="Ir para o Início (OPEN TV)"
      >
        <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-theme-accent flex items-center justify-center text-black font-black shadow-md shadow-theme-accent-sm transition group-hover:scale-105">
          <Tv className="w-5 h-5 stroke-[2.5]" />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-black text-base md:text-lg text-white tracking-wider group-hover:text-theme-accent-light transition">OPEN TV</span>
        </div>
      </div>

      {/* Main Navigation Tabs (Visível em Tablets Horizontais, Laptops e TVs - lg: 1024px+) */}
      <nav className="hidden lg:flex items-center gap-1.5 md:gap-2.5 py-1 px-1 overflow-visible">
        {navTabs.map((item) => {
          const Icon = item.icon;
          const isActive = !item.isRefresh && activeTab === item.id;

          if (item.isRefresh) {
            return (
              <button
                key={item.id}
                id="nav-tab-refresh"
                onClick={item.action}
                disabled={isRefreshing}
                className={`px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-semibold flex items-center gap-2 transition-colors duration-150 cursor-pointer focus:outline-none shrink-0 ${
                  isRefreshing
                    ? 'bg-zinc-800 text-white shadow-sm ring-1 ring-white/10'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-900 border border-transparent hover:border-zinc-800 focus:bg-zinc-900 focus:text-white focus:ring-2 focus:ring-theme-accent/60'
                }`}
                title="Buscar novos canais, filmes e séries no servidor"
              >
                <RefreshCw className={`w-4 h-4 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span>{item.label}</span>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              id={`nav-tab-${item.id}`}
              onClick={item.action}
              className={`px-3.5 md:px-4 py-2 rounded-xl text-xs md:text-sm font-bold flex items-center gap-2 transition-colors duration-150 cursor-pointer focus:outline-none shrink-0 ${
                isActive
                  ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-900 border border-transparent hover:border-zinc-800 focus:bg-zinc-900 focus:text-white focus:ring-2 focus:ring-theme-accent/60'
              }`}
            >
              <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'stroke-[2.5]' : ''}`} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Right Side: Unified Menu Button & Clock */}
      <div
        className="flex items-center gap-2 sm:gap-2.5 shrink-0"
        ref={menuRef}
        onMouseEnter={cancelCloseTimer}
        onMouseLeave={() => {
          if (isMenuOpen) {
            startCloseTimer(2500);
          }
        }}
        onFocusCapture={cancelCloseTimer}
        onBlurCapture={(e) => {
          if (menuRef.current && !menuRef.current.contains(e.relatedTarget as Node)) {
            if (isMenuOpen) {
              startCloseTimer(2500);
            }
          }
        }}
      >
        {/* Single Unified Hub Button */}
        <div className="relative">
          <button
            ref={toggleBtnRef}
            id="nav-menu-toggle-btn"
            onClick={() => {
              cancelCloseTimer();
              if (!isMasterProfile) {
                onOpenProfiles();
                return;
              }
              setIsMenuOpen((prev) => !prev);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'Down') {
                e.preventDefault();
                cancelCloseTimer();
                if (!isMasterProfile) {
                  onOpenProfiles();
                  return;
                }
                setIsMenuOpen(true);
                setTimeout(() => {
                  const first = menuRef.current?.querySelector<HTMLElement>('button[role="menuitem"], button');
                  first?.focus();
                }, 50);
              }
            }}
            className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl border text-xs font-bold transition-all duration-200 cursor-pointer shadow-sm focus:outline-none shrink-0 ${
              isMenuOpen
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/50 shadow-sm'
                : 'bg-zinc-900/90 hover:bg-zinc-800 text-zinc-200 hover:text-white border-zinc-800/80 hover:border-zinc-700 focus:ring-2 focus:ring-theme-accent/60'
            }`}
            title={isMasterProfile ? `Perfil: ${currentProfile?.name || 'Master'} - Menu de Opções e Configurações` : "Clique para Trocar de Perfil"}
            aria-haspopup={isMasterProfile ? "true" : undefined}
            aria-expanded={isMasterProfile ? isMenuOpen : undefined}
          >
            <span className="text-base leading-none">{currentProfile?.avatar || '👑'}</span>
            {isMasterProfile && (
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  isMenuOpen ? 'rotate-180 text-amber-400 stroke-[2.5]' : 'text-zinc-400'
                }`}
              />
            )}
          </button>

          {/* Unified Dropdown Menu Popover (Apenas para Master) */}
          {isMenuOpen && isMasterProfile && (
            <div
              role="menu"
              data-modal="true"
              data-dropdown="true"
              aria-orientation="vertical"
              className="absolute right-0 top-full mt-2.5 w-76 sm:w-84 rounded-2xl bg-zinc-900/95 border border-zinc-800 shadow-2xl backdrop-blur-2xl p-3 z-50 animate-in fade-in slide-in-from-top-2 duration-150 flex flex-col gap-2 ring-1 ring-white/10 max-h-[85vh] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-zinc-800"
            >
              {/* Item 0: Trocar de Perfil (Profile Header Summary) */}
              <button
                ref={(el) => {
                  itemRefs.current[0] = el;
                }}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => handleMenuItemKeyDown(0, e)}
                type="button"
                onClick={() => {
                  cancelCloseTimer();
                  setIsMenuOpen(false);
                  onOpenProfiles();
                }}
                className="w-full px-3.5 py-3 rounded-xl bg-zinc-950/80 hover:bg-zinc-800/80 border border-zinc-800/70 hover:border-theme-accent/50 focus:bg-zinc-800 focus:border-theme-accent focus:ring-2 focus:ring-theme-accent/50 focus:outline-none flex items-center justify-between text-left transition cursor-pointer group"
                title="Clique para alternar perfil"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="w-9 h-9 rounded-xl border flex items-center justify-center text-lg shrink-0 shadow-inner group-hover:scale-105 transition bg-theme-accent-subtle border-theme-accent-subtle"
                  >
                    {currentProfile?.avatar || '👤'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold text-white group-hover:text-theme-accent-light transition truncate">
                      {currentProfile?.name || 'Perfil Padrão'}
                    </div>
                    <div
                      className="text-[10px] font-medium flex items-center gap-1 text-theme-accent"
                    >
                      <CheckCircle2 className="w-3 h-3 text-current shrink-0" />
                      <span>{currentProfile?.isKids ? 'Modo Infantil Ativo' : 'Perfil Ativo'}</span>
                    </div>
                  </div>
                </div>

                <span className="text-[10px] px-2 py-0.5 rounded-lg bg-zinc-800 text-zinc-400 group-hover:bg-theme-accent group-hover:text-black font-bold transition">
                  Trocar
                </span>
              </button>

              {/* Item 1: Gerenciar Perfis & Controle Parental */}
              <button
                ref={(el) => {
                  itemRefs.current[1] = el;
                }}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => handleMenuItemKeyDown(1, e)}
                onClick={() => {
                  cancelCloseTimer();
                  setIsMenuOpen(false);
                  onOpenProfiles(null, true);
                }}
                className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950/40 hover:bg-zinc-800 border border-zinc-800/60 focus:bg-zinc-800 focus:border-theme-accent focus:ring-2 focus:ring-theme-accent/50 focus:outline-none text-left flex items-center gap-3 transition cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-xl bg-theme-accent/10 border border-theme-accent/20 flex items-center justify-center text-theme-accent shrink-0">
                  <Shield className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-zinc-200 group-hover:text-white group-focus:text-white flex items-center justify-between">
                    <span>Perfis &amp; Parental</span>
                    <span className="text-[9px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-mono">
                      Master
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-400 truncate">
                    Avatares, faixas etárias e bloqueio de categorias
                  </div>
                </div>
              </button>

              {/* Item: Aparelhos Conectados */}
              <button
                role="menuitem"
                tabIndex={0}
                onClick={() => {
                  cancelCloseTimer();
                  setIsMenuOpen(false);
                  openDevicesModal();
                }}
                className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950/40 hover:bg-zinc-800 border border-zinc-800/60 focus:bg-zinc-800 focus:border-theme-accent focus:ring-2 focus:ring-theme-accent/50 focus:outline-none text-left flex items-center gap-3 transition cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-xl bg-theme-accent-subtle border border-theme-accent-subtle flex items-center justify-center text-theme-accent shrink-0">
                  <Smartphone className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-zinc-200 group-hover:text-white group-focus:text-white flex items-center justify-between">
                    <span>Aparelhos Conectados</span>
                    <span className="text-[9px] px-1.5 py-0.2 rounded bg-theme-accent-subtle text-theme-accent-light font-mono font-bold">
                      Até 5
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-400 truncate">
                    Gerenciar e desconectar dispositivos da conta
                  </div>
                </div>
              </button>

              {/* Item: Atualizar Lista (V8.1.9 — só no menu do perfil Master; a Web continua atualizando sozinha) */}
              <button
                role="menuitem"
                tabIndex={0}
                type="button"
                disabled={isRefreshing}
                onClick={() => {
                  cancelCloseTimer();
                  setIsMenuOpen(false);
                  handleRefreshList();
                }}
                className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950/40 hover:bg-zinc-800 border border-zinc-800/60 focus:bg-zinc-800 focus:border-theme-accent focus:ring-2 focus:ring-theme-accent/50 focus:outline-none text-left flex items-center gap-3 transition cursor-pointer group disabled:opacity-60"
                title="Buscar novos canais, filmes e séries no servidor"
              >
                <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-zinc-200 group-hover:text-white group-focus:text-white">
                    {isRefreshing ? 'Atualizando lista...' : 'Atualizar Lista'}
                  </div>
                  <div className="text-[10px] text-zinc-400 truncate">
                    Buscar novos canais, filmes e séries
                  </div>
                </div>
              </button>

              {/* Item: Baixar Aplicativo Android / TV Box (APK) */}
              <a
                href="/app"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  cancelCloseTimer();
                  setIsMenuOpen(false);
                }}
                className="w-full px-3.5 py-2.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-left flex items-center gap-3 transition cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0">
                  <Download className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-amber-300 group-hover:text-amber-200 flex items-center justify-between">
                    <span>Baixar Aplicativo (APK)</span>
                    <span className="text-[9px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-mono font-bold">
                      Android / TV
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-400 truncate">
                    Instalar app nativo no celular ou TV Box
                  </div>
                </div>
              </a>


              {/* ADMIN-ONLY ADVANCED TOOLS */}
              {isAdmin && isMasterProfile && (
                <>
                  <div className="pt-2 border-t border-zinc-800/80 my-1">
                    <div className="text-[10px] text-amber-400 font-bold uppercase tracking-wider px-2 mb-1">
                      Painel do Administrador
                    </div>
                  </div>

                  {/* Item 2: Abrir Gerenciador do Sistema */}
                  <a
                    ref={(el) => {
                      itemRefs.current[2] = el;
                    }}
                    role="menuitem"
                    tabIndex={0}
                    onKeyDown={(e) => handleMenuItemKeyDown(2, e)}
                    href="/gerenciador"
                    onClick={() => {
                      cancelCloseTimer();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 focus:bg-amber-500/30 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/50 focus:outline-none text-left flex items-center gap-3 transition cursor-pointer group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0">
                      <Settings className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-zinc-200 group-hover:text-white group-focus:text-white flex items-center justify-between">
                        <span>Central Gerenciador</span>
                        <span className="text-[9px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-mono">
                          /gerenciador
                        </span>
                      </div>
                      <div className="text-[10px] text-zinc-400 truncate">
                        Acessar Gestão de Clientes, Listas, DNS e Servidores
                      </div>
                    </div>
                  </a>
                </>
              )}
            </div>
          )}
        </div>

        {/* Indicador de Versão (Visível em todos os dispositivos) & Relógio (Apenas Desktop/TV) */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="px-2 py-1 rounded-lg bg-theme-accent-subtle border border-theme-accent-border text-[10px] font-mono font-bold text-theme-accent-light shrink-0">
            v{APP_VERSION}
          </div>
          <div className="hidden sm:flex items-center px-3 py-1.5 rounded-xl bg-zinc-900/60 border border-zinc-800 text-xs font-mono font-bold text-zinc-400 shrink-0">
            <span>{timeStr}</span>
          </div>
        </div>
      </div>
    </header>

    {/* Bottom Navigation Bar para Tablet Vertical e Smartphones (Telas < 1024px / lg:hidden) */}
    {!hideMobileBottomBar && (
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-zinc-950/95 backdrop-blur-2xl border-t border-zinc-800/90 flex items-center justify-around py-1.5 px-2 pb-[max(env(safe-area-inset-bottom,0px),8px)] shadow-2xl">
        {[
          { id: 'home', label: 'Início', icon: Tv },
          { id: 'live', label: 'Ao Vivo', icon: Radio },
          { id: 'movies', label: 'Filmes', icon: Film },
          { id: 'series', label: 'Séries', icon: Tv2 },
          { id: 'favorites', label: 'Favoritos', icon: Star },
        ].map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleTabClick(item.id as any)}
              className={`bottom-nav-btn flex flex-col items-center justify-center py-1.5 px-2.5 sm:px-4 rounded-2xl cursor-pointer select-none relative group ${
                isActive
                  ? 'active font-black scale-105'
                  : 'text-zinc-400'
              }`}
              style={
                isActive
                  ? {
                      color: 'var(--theme-accent, #f59e0b)',
                      backgroundColor: 'var(--theme-accent-subtle, rgba(245, 158, 11, 0.15))',
                      boxShadow: '0 0 14px var(--theme-accent-glow, rgba(245, 158, 11, 0.25))',
                    }
                  : undefined
              }
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = 'var(--theme-accent, #f59e0b)';
                  e.currentTarget.style.backgroundColor = 'var(--theme-accent-subtle, rgba(245, 158, 11, 0.12))';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = '';
                  e.currentTarget.style.backgroundColor = '';
                }
              }}
              title={item.label}
            >
              <Icon className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform ${isActive ? 'stroke-[2.5] scale-110' : 'group-hover:scale-105'}`} />
              <span className="text-[10px] sm:text-xs mt-1 font-bold tracking-tight">{item.label}</span>
              {isActive && (
                <span
                  className="absolute -top-1 w-6 h-0.5 rounded-full"
                  style={{ backgroundColor: 'var(--theme-accent, #f59e0b)' }}
                />
              )}
            </button>
          );
        })}
      </nav>
    )}
  </>
  );
};

